import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import moment from 'moment';

import * as http from 'utils/http';

import config from 'config';

import {
  StyledTitle,
  StyledRow,
  StyledRowTitle,
  StyledGuessPlatform,
  StyledContainer,
  StyledContent,
} from './styled';

import { Flexer, Link, Button, Spacer } from '@ontruck/orc';

const META_CHANGES = [
  'modified_at',
  'modified_by_id',
  'change_referer',
  'change_type',
  'change_app',
  'change_query',
  'change_by_id',
  'change_by_username',
  'change_address',
  'change_user_agent',
  'history_date',
  'change_date',
];

const SHIPPER_APP_REGEX = new RegExp('/new/(summary|payment)');
const SHIPPER_FORM_REGEX = new RegExp('TO_BE_DETERMINED');
const MERCURY_REGEX = new RegExp('(/mercury)?/(new|intransit|finished|edit)');
const API_REGEX = new RegExp('/api');

const filterEmptyEntries = (entries = []) => {
  return entries.filter(
    (entry) => Object.keys(entry).filter((key) => META_CHANGES.indexOf(key) < 0).length > 0
  );
};

const formatChangeType = (field = '', status) => {
  if (status) {
    const arr = String(status).split('->');
    if (arr.length === 2) return 'Moved to ' + arr[1];
  }

  switch (field) {
    case '+':
      return 'Added';
    case '~':
      return 'Modified';
    case '-':
      return 'Removed';
    default:
      return '???';
  }
};

const formatModifiedAt = (field = '', dateFormats) => {
  const arr = String(field).split('->');

  if (arr.length !== 2) return '???';

  return moment(arr[1]).format(dateFormats.full_datetime_ss);
};

const joinLoads = (a, b) => {
  return {
    number_of_items: (a.number_of_items || 0) + (b.number_of_items || 0),
    total_weight: (a.total_weight || 0) + (b.total_weight || 0),
    item_width: Math.max(a.item_width || 0, b.item_width || 0),
    item_length: Math.max(a.item_length || 0, b.item_length || 0),
    item_height: Math.max(a.item_height || 0, b.item_height || 0),
    pallet_type: a.pallet_type || b.pallet_type,
    temperature_range: [].concat(a.temperature_range || '[]').concat(b.temperature_range || []),
  };
};

const formatLoadsDump = (field = '') => {
  const arr = String(field).split('->');

  if (arr.length !== 2) return '???';

  try {
    if (arr[0] === 'None ') arr[0] = '[]';
    if (arr[1] === ' None') arr[1] = '[]';
    let oldLoad = JSON.parse(
      (arr[0] || '[]').trim().replace(/'/g, '"').replace('True', 'true').replace('False', 'false')
    ).reduce(joinLoads, {});
    let newLoad = JSON.parse(
      (arr[1] || '[]').trim().replace(/'/g, '"').replace('True', 'true').replace('False', 'false')
    ).reduce(joinLoads, {});

    oldLoad.temperature_range = JSON.stringify(oldLoad.temperature_range);
    newLoad.temperature_range = JSON.stringify(newLoad.temperature_range);

    return (
      <Spacer pLeft="S">
        {Object.keys({ ...oldLoad, ...newLoad }).map((key) => (
          <div key={key}>
            <span>{key}</span>: {String(oldLoad[key])}&nbsp;&#8658;&nbsp;{String(newLoad[key])}
          </div>
        ))}
      </Spacer>
    );
  } catch (err) {
    //console.warn(err);
    return '???';
  }
};

const formatExtras = (field = '') => {
  const arr = String(field).split('->');

  if (arr.length !== 2) return '???';

  try {
    if (arr[0] === 'None ') arr[0] = '[]';
    if (arr[1] === ' None') arr[1] = '[]';
    const oldExtras = JSON.parse(
      (arr[0] || '[]').trim().replace(/'/g, '"').replace('True', 'true').replace('False', 'false')
    );
    const newExtras = JSON.parse(
      (arr[1] || '[]').trim().replace(/'/g, '"').replace('True', 'true').replace('False', 'false')
    );

    const extras = oldExtras
      .concat(newExtras)
      .map((extra) => extra.concept)
      .sort()
      .filter((val, index, arr) => arr.indexOf(val) === index) // AKA uniq
      .map((concept) => {
        const from = oldExtras.filter((extra) => extra.concept === concept);
        const to = newExtras.filter((extra) => extra.concept === concept);

        return {
          concept,
          from: from[0] ? `${from[0].amount}${from[0].type === 'PERCENT' ? '%' : ''}` : 'None',
          to: to[0] ? `${to[0].amount}${to[0].type === 'PERCENT' ? '%' : ''}` : 'None',
        };
      });

    return (
      <Spacer pLeft="S">
        {extras.map(({ concept, from, to }) => (
          <div key={concept}>
            <span>{concept}</span>: {from}&nbsp;<strong>&#8658;&nbsp;</strong>
            {to}
          </div>
        ))}
      </Spacer>
    );
  } catch (err) {
    return '???';
  }
};

const isNoOp = (field = '') => {
  const arr = String(field).split('->');
  if (arr.length !== 2) return false;

  return (arr[0] || '').trim() === (arr[1] || '').trim();
};

const hilightArrow = (field = '') => {
  const arr = String(field).split('->');
  if (arr.length !== 2) return <span>{String(field)}</span>;

  return (
    <span>
      {(arr[0] || '').trim()}
      &nbsp;&nbsp;&#8658;&nbsp;&nbsp;
      {(arr[1] || '').trim()}
    </span>
  );
};

const guessPlatform = (referer, app) => {
  if (app && app !== 'Unknown') return app;
  if (referer && referer.match(SHIPPER_APP_REGEX)) return 'Shipper App 2.0';
  if (referer && referer.match(SHIPPER_FORM_REGEX)) return 'Shipper Form Legacy';
  if (referer && referer.match(MERCURY_REGEX)) return 'TMS';
  if (referer && referer.match(API_REGEX)) return 'API';
  return `Unknown (${referer})`;
};

class History extends Component {
  constructor(props) {
    super(props);

    this.state = {
      history: [],
      showAll: false,
    };

    this.getHistory = this.getHistory.bind(this);
  }

  getHistory() {
    const journey = this.props.journey;
    if (!journey) {
      return;
    }

    http
      .get(journey.history)
      .then(filterEmptyEntries)
      .then((history) => this.setState({ history }));
  }

  render() {
    const { journey, intl, maxItems, ...props } = this.props;

    const { history, showAll } = this.state;

    // TODO: add some strings
    const t = intl.messages.components.ShipmentModalLateral;
    const dateFormats = intl.messages.date_formats;

    const HistoryRow = ({ entry, index }) => (
      <StyledRow>
        <Flexer.Child flexGrow="1">
          <StyledRowTitle>
            <span>{formatChangeType(entry.change_type, entry.status)}</span>
            &nbsp;-&nbsp;
            <Link
              href={`${config.API_URL}users/${entry.change_by_id}/`}
              target="_blank"
              rel="noopener"
              text={entry.change_by_username}
            />
            &nbsp;-&nbsp;
            <span>{formatModifiedAt(entry.modified_at, dateFormats)}</span>
          </StyledRowTitle>
          <StyledGuessPlatform>
            {guessPlatform(entry.change_referer, entry.change_app)}
          </StyledGuessPlatform>
          <div>
            {Object.keys(entry)
              .filter((key) => META_CHANGES.indexOf(key) < 0)
              .filter((key) => !isNoOp(entry[key]))
              .map((key) => (
                <div key={'history_entry_' + index + '_' + key}>
                  {key === 'loads_dump' ? (
                    <div>
                      {key}: {formatLoadsDump(entry[key])}
                    </div>
                  ) : key === 'extra_prices' || key === 'extra_costs' ? (
                    <div>
                      {key}: {formatExtras(entry[key])}
                    </div>
                  ) : (
                    <div>
                      {key}: {hilightArrow(entry[key])}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </Flexer.Child>
        <Flexer.Child>
          <h4>
            <Link
              href={`${config.API_URL}journeys/${journey?.id}/history/`}
              target="_blank"
              rel="noopener"
              text={t.history_view_api}
            />
          </h4>
        </Flexer.Child>
      </StyledRow>
    );

    const historyElements = showAll ? history || [] : (history || []).slice(0, maxItems);

    return (
      <StyledContainer {...props}>
        <StyledTitle mBottom="XS">
          <Flexer.Child flexGrow="1">{t.history_title}</Flexer.Child>
          <Flexer.Child flexGrow="0">
            <Link link onClick={this.getHistory} noWrap text={t.reload_history} weight="bold" />
          </Flexer.Child>
        </StyledTitle>
        {!history || history.length <= 0 ? (
          <div>
            {
              <Button
                nude
                onClick={this.getHistory}
                text={t.history_fetch_data}
                fullWidth
                weight="bold"
              />
            }
          </div>
        ) : (
          <StyledContent>
            {historyElements.map((entry, index) => (
              <HistoryRow entry={entry} index={index} key={index} />
            ))}
            {history.length > historyElements.length && !showAll ? (
              <Button
                onClick={() => this.setState({ showAll: true })}
                text={t.history_show_more}
                fullWidth
                nude
                weight="bold"
              />
            ) : null}
          </StyledContent>
        )}
      </StyledContainer>
    );
  }
}

History.defaultProps = {
  maxItems: 2,
};

History.propTypes = {
  journey: PropTypes.object,
  intl: PropTypes.any,
  maxItems: PropTypes.number,
};

export default injectIntl(History);
